import React, { useState, useEffect } from "react";

const useBreakpoint = () => {
  const [width, setWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1024
  );
  useEffect(() => {
    const handler = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  if (width < 480) return "mobile";
  if (width < 768) return "mobileLarge";
  if (width < 1024) return "tablet";
  return "desktop";
};

const DiningTerms = () => {
  const bp = useBreakpoint();

  const isMobile = bp === "mobile" || bp === "mobileLarge";
  const isTablet = bp === "tablet";
  const isDesktop = bp === "desktop";
  const desktopLeftOffset = "200px";

  const styles = {
    wrapper: {
      minHeight: "100vh",
      backgroundColor: "#ffffff",
      color: "#1a1a1a",
    },
    main: {
      maxWidth: isDesktop ? "1150px" : isTablet ? "680px" : "100%",
      margin: isDesktop ? `0 auto 0 ${desktopLeftOffset}` : "0 auto",
      padding: isMobile
        ? bp === "mobile"
          ? "1.25rem 0.9rem"
          : "1.5rem 1.1rem"
        : isTablet
        ? "2rem 1.5rem"
        : "3rem 2rem",
    },
    h1: {
      fontSize:
        bp === "mobile"
          ? "1.45rem"
          : bp === "mobileLarge"
          ? "1.65rem"
          : isTablet
          ? "2rem"
          : "2.4rem",
           marginTop: isMobile ? "4rem" : 0,
      fontWeight: "900",
      marginBottom: "0.3rem",
      lineHeight: "1.2",
    },
    effectiveDate: {
      fontSize: isMobile ? "0.82rem" : "0.95rem",
      fontStyle: "italic",
      color: "#444",
      marginBottom: isMobile ? "1.1rem" : isTablet ? "1.5rem" : "2rem",
    },
    intro: {
      fontSize: isMobile ? "0.92rem" : "1rem",
      lineHeight: isMobile ? "1.65" : "1.8",
      marginBottom: isMobile ? "0.5rem" : "0.75rem",
      color: "#1a1a1a",
    },
    h3: {
      fontSize: isMobile ? "0.93rem" : isTablet ? "1rem" : "1.05rem",
      fontWeight: "700",
      marginTop: isMobile ? "1.3rem" : isTablet ? "1.6rem" : "2rem",
      marginBottom: "0.4rem",
    },
    p: {
      fontSize: isMobile ? "0.92rem" : "1rem",
      lineHeight: isMobile ? "1.65" : "1.8",
      marginBottom: "0.75rem",
      color: "#1a1a1a",
    },
    ul: {
      paddingLeft: isMobile ? "1rem" : "1.5rem",
      marginBottom: "0.75rem",
    },
    li: {
      fontSize: isMobile ? "0.92rem" : "1rem",
      lineHeight: isMobile ? "1.65" : "1.8",
      color: "#1a1a1a",
      marginBottom: "0.25rem",
    },
  };

  return (
    <div style={styles.wrapper}>
      <main style={styles.main}>
        <h1 style={styles.h1}>Dining Table Booking Terms &amp; Conditions</h1>

        <p style={styles.intro}>
          Please read these Terms &amp; Conditions carefully before making a
          table reservation at any restaurant listed on the Berymo platform. By
          completing a booking, you agree to be bound by the following terms.
        </p>

        <h3 style={styles.h3}>1. Table Reservation</h3>
        <p style={styles.p}>
          A table reservation is a confirmed booking of a dining table at a
          partner restaurant for a specified date, time, and party size. Your
          reservation is subject to availability and the restaurant's seating
          capacity at the time of booking.
        </p>
        <ul style={styles.ul}>
          <li style={styles.li}>
            Reservations must be made in advance through the Berymo app or
            website.
          </li>
          <li style={styles.li}>
            A booking confirmation will be sent to your registered email or
            phone number once the reservation is confirmed.
          </li>
          <li style={styles.li}>
            The restaurant reserves the right to allocate a suitable table
            based on party size and availability.
          </li>
        </ul>

        <h3 style={styles.h3}>2. Booking Fees</h3>
        <p style={styles.p}>
          Some restaurants may charge a non-refundable table booking fee at the
          time of reservation. This fee is set by the individual restaurant and
          will be clearly displayed before you confirm your booking.
        </p>
        <ul style={styles.ul}>
          <li style={styles.li}>
            Booking fees are charged per reservation, not per person, unless
            stated otherwise by the restaurant.
          </li>
          <li style={styles.li}>
            In some cases, the booking fee may be deducted from your final
            dining bill at the restaurant's discretion.
          </li>
          <li style={styles.li}>
            Berymo is not responsible for any additional charges levied
            directly by the restaurant.
          </li>
        </ul>

        <h3 style={styles.h3}>3. Arrival and Seating</h3>
        <p style={styles.p}>
          You are required to arrive at the restaurant within the grace period
          specified in your booking confirmation. Failure to arrive within the
          stipulated time may result in the table being released to other
          guests.
        </p>
        <ul style={styles.ul}>
          <li style={styles.li}>
            A standard grace period of 15 minutes is applicable unless the
            restaurant specifies otherwise.
          </li>
          <li style={styles.li}>
            If you are running late, please inform the restaurant directly
            using the contact details provided in your confirmation.
          </li>
          <li style={styles.li}>
            Tables held beyond the grace period without prior notice may be
            treated as a no-show.
          </li>
        </ul>

        <h3 style={styles.h3}>4. Cancellations and Modifications</h3>
        <p style={styles.p}>
          Cancellation and modification policies vary by restaurant and will be
          displayed during the booking process. You are advised to review them
          carefully before confirming your reservation.
        </p>
        <ul style={styles.ul}>
          <li style={styles.li}>
            Free cancellations are permitted up to the cancellation deadline
            specified by the restaurant (typically 2–24 hours before the
            reservation time).
          </li>
          <li style={styles.li}>
            Late cancellations or no-shows may incur a cancellation charge as
            defined by the restaurant's policy.
          </li>
          <li style={styles.li}>
            Changes to party size or reservation time are subject to
            availability and must be made before the cancellation deadline.
          </li>
          <li style={styles.li}>
            To cancel or modify your reservation, visit "My Bookings" in the
            Berymo app or contact Berymo Support.
          </li>
        </ul>

        <h3 style={styles.h3}>5. No-Show Policy</h3>
        <p style={styles.p}>
          If you fail to arrive for your reservation without prior cancellation,
          it will be marked as a no-show. Repeated no-shows may result in
          restrictions on your ability to make future table reservations through
          Berymo.
        </p>
        <ul style={styles.ul}>
          <li style={styles.li}>
            A no-show fee may be charged as per the restaurant's stated policy.
          </li>
          <li style={styles.li}>
            Any pre-paid booking fee for a no-show reservation will not be
            refunded unless the restaurant's policy explicitly states otherwise.
          </li>
        </ul>

        <h3 style={styles.h3}>6. Special Requests</h3>
        <p style={styles.p}>
          You may submit special requests (such as dietary requirements,
          accessibility needs, seating preferences, or occasion-related
          requests) at the time of booking. These requests are subject to the
          restaurant's availability and discretion.
        </p>
        <ul style={styles.ul}>
          <li style={styles.li}>
            Special requests are not guaranteed and do not form part of the
            confirmed reservation.
          </li>
          <li style={styles.li}>
            For medical or allergy-related requirements, we recommend contacting
            the restaurant directly prior to your visit.
          </li>
        </ul>

        <h3 style={styles.h3}>7. Restaurant Policies and Right of Admission</h3>
        <p style={styles.p}>
          Each restaurant listed on Berymo operates independently and may have
          its own house policies including dress codes, minimum spends, age
          restrictions, or private event closures.
        </p>
        <ul style={styles.ul}>
          <li style={styles.li}>
            Berymo is not responsible for any policies enforced by individual
            restaurants.
          </li>
          <li style={styles.li}>
            The restaurant reserves the right to refuse entry or service in
            accordance with applicable laws and their internal policies.
          </li>
          <li style={styles.li}>
            Any disputes regarding restaurant policies must be resolved directly
            with the restaurant.
          </li>
        </ul>

        <h3 style={styles.h3}>8. Group and Private Dining</h3>
        <p style={styles.p}>
          Reservations for large groups (typically 8 or more guests) or private
          dining events may be subject to separate terms, including minimum
          spend requirements, advance deposits, and customised menus.
        </p>
        <ul style={styles.ul}>
          <li style={styles.li}>
            Group bookings must be confirmed directly with the restaurant in
            addition to the Berymo reservation.
          </li>
          <li style={styles.li}>
            Deposits paid for private dining are governed by the restaurant's
            event booking agreement and may be non-refundable.
          </li>
        </ul>

        <h3 style={styles.h3}>9. Pre-Orders and Set Menus</h3>
        <p style={styles.p}>
          Certain restaurants may require pre-ordering of meals or selection of
          a set menu at the time of booking, particularly for special occasions
          or festive periods.
        </p>
        <ul style={styles.ul}>
          <li style={styles.li}>
            Pre-orders confirmed at the time of booking are binding and may not
            be changed after the cancellation deadline.
          </li>
          <li style={styles.li}>
            Payments made for pre-ordered meals are subject to the restaurant's
            refund and cancellation policy.
          </li>
        </ul>

        <h3 style={styles.h3}>10. Liability</h3>
        <p style={styles.p}>
          Berymo acts as an intermediary platform between diners and
          restaurants. We are not liable for the quality of food, service,
          hygiene standards, or any experiences at the restaurant. Any
          complaints related to your dining experience should be directed to the
          restaurant management.
        </p>
        <ul style={styles.ul}>
          <li style={styles.li}>
            Berymo's liability is limited to the booking fee paid through the
            platform, where applicable.
          </li>
          <li style={styles.li}>
            We are not responsible for any loss, injury, or damage incurred
            during your visit to the restaurant.
          </li>
        </ul>

        <h3 style={styles.h3}>11. Disputes and Support</h3>
        <p style={styles.p}>
          For billing disputes, reservation issues, or any concerns related to
          your table booking, contact Berymo Support at support@berymo.com with
          your booking reference number, date, and details of the issue. We aim
          to resolve all queries within 3–5 business days.
        </p>

        <h3 style={styles.h3}>12. Updates to These Terms</h3>
        <p style={styles.p}>
          Berymo reserves the right to update these Terms &amp; Conditions at
          any time. The most recent version will be available on this page. Your
          continued use of the dining reservation feature constitutes acceptance
          of any revised terms.
        </p>
      </main>
    </div>
  );
};

export default DiningTerms;
